/**
 * The Pricing page — a public plan comparison that works signed out.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C04C; reworked for the owner
 * walkthrough findings F-34 (signed-out visitors saw "authentication is
 * required") and F-35 (prices shown as minor units).
 *
 * WHERE THE PLANS COME FROM
 *   `api/billing.ts::fetchPublicPlans()` → `GET /v1/billing/plans`, a public,
 *   read-only route that serves only catalog rows whose visibility is exactly
 *   `public`, with display fields only (no grant keys, visibility, approval
 *   status or price reference). This page never invents a plan name, price or
 *   feature list; every card traces to one catalog row's `id`.
 *
 * WHY THE PAGE MAY SHOW "No plan is open for sign-up yet"
 *   Public visibility requires recorded owner approval, and
 *   `commercial_product_catalog.sellable()` is the pricing approval gate.
 *   Both are read from the server, never decided here.
 *
 * PRICES
 *   A price is shown only when the server sent an amount and a currency, and
 *   it is formatted as money by `lib/money.ts` ("$39.00 / month"). Otherwise
 *   the card says the price is shown at checkout. No amount is written here.
 *
 * WHO HANDLES NAVIGATION
 *   `onRequireLogin`/`onCheckoutStart` are optional host callbacks, defaulting
 *   to a plain `window.location.assign`.
 */

import React, { useCallback, useEffect, useState } from 'react';

import { currentSession } from '../../api/auth';
import {
  BillingApiError,
  abandonCheckoutIntent,
  type StartCheckoutInput,
  PublicPlan,
  fetchPublicPlans,
  rememberPendingCheckoutSessionRef,
  startCheckout,
} from '../../api/billing';
import { ApiClientError } from '../../api/http';
import { formatPrice } from '../../lib/money';

export interface PricingPageProps {
  /** Defaults to navigating this tab to `/login?next=/pricing`. */
  onRequireLogin?: () => void;
  /** Defaults to navigating this tab to the server's `redirect_url`. */
  onCheckoutStart?: (redirectUrl: string) => void;
}

function PricingComingSoon({ reason }: { reason: string }) {
  return (
    <span className="pricing-card-status" data-state="Withheld">
      Coming soon — {reason}
    </span>
  );
}

interface ServerTrialTerms {
  eligible: boolean;
  duration_days: number;
  card_required: boolean;
}

function trialTerms(entry: PublicPlan): ServerTrialTerms | null {
  // The billing catalog must supply these fields. No offer-id list or trial
  // length is inferred in the browser. An absent/malformed projection has no
  // trial CTA, even if a commercial product happens to be sellable.
  const value: unknown = entry.trial;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const terms = value as Record<string, unknown>;
  if (
    typeof terms.eligible !== 'boolean' ||
    typeof terms.duration_days !== 'number' ||
    !Number.isInteger(terms.duration_days) ||
    terms.duration_days <= 0 ||
    terms.card_required !== true
  ) return null;
  return terms as unknown as ServerTrialTerms;
}

export function PricingCard({
  entry,
  authenticated,
  busy,
  onSubscribe,
}: {
  entry: PublicPlan;
  authenticated: boolean | null;
  busy: boolean;
  onSubscribe: (entry: PublicPlan, trialRequested: boolean) => void;
}) {
  const purchasable = entry.sellable;
  const trial = trialTerms(entry);
  const trialAvailable = Boolean(trial && trial.eligible);
  const price = formatPrice(
    entry.pricing.amount_minor,
    entry.pricing.currency,
    entry.pricing.billing_period,
  );
  return (
    <article className="pricing-card" data-entry-id={entry.id} data-sellable={purchasable}>
      <h2>{entry.name}</h2>
      {entry.note ? <p className="pricing-card-note">{entry.note}</p> : null}
      {trialAvailable && trial ? (
        <p className="pricing-card-trial-terms">
          {trial.duration_days}-day free trial — card required at signup. You will not be charged
          until the trial ends.
        </p>
      ) : null}

      {purchasable ? (
        <>
          <p className="pricing-card-price">{price ?? 'Price shown at checkout'}</p>
          <button type="button" disabled={busy} onClick={() => onSubscribe(entry, trialAvailable)}>
            {authenticated === false
              ? 'Sign in to continue'
              : trialAvailable ? 'Start free trial' : 'Subscribe'}
          </button>
        </>
      ) : entry.pricing.model === 'contact_sales' ? (
        <PricingComingSoon reason="contact us for pricing" />
      ) : (
        <PricingComingSoon reason="not yet open for sign-up" />
      )}
    </article>
  );
}

export function PricingPage({ onRequireLogin, onCheckoutStart }: PricingPageProps = {}): React.ReactElement {
  const [entries, setEntries] = useState<PublicPlan[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [busyEntryId, setBusyEntryId] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState('');
  const [stuckIntent, setStuckIntent] = useState<StartCheckoutInput | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPublicPlans()
      .then((plans) => {
        if (cancelled) return;
        setEntries(plans);
        setUnavailable(false);
      })
      .catch(() => {
        if (cancelled) return;
        setEntries(null);
        setUnavailable(true);
      });
    currentSession()
      .then((session) => {
        if (!cancelled) setAuthenticated(session !== null);
      })
      .catch(() => {
        if (!cancelled) setAuthenticated(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const requireLogin = useCallback(() => {
    if (onRequireLogin) {
      onRequireLogin();
      return;
    }
    window.location.assign('/login?next=/pricing');
  }, [onRequireLogin]);

  const beginCheckout = useCallback(
    (entry: PublicPlan, trialRequested: boolean) => {
      if (!authenticated) {
        requireLogin();
        return;
      }
      setBusyEntryId(entry.id);
      setCheckoutError('');
      setStuckIntent(null);
      const input = { offerId: entry.id, trialRequested };
      startCheckout(input)
        .then((handoff) => {
          rememberPendingCheckoutSessionRef(handoff.session_ref);
          if (onCheckoutStart) {
            onCheckoutStart(handoff.redirect_url);
          } else {
            window.location.assign(handoff.redirect_url);
          }
        })
        .catch((failure) => {
          setBusyEntryId(null);
          if (failure instanceof BillingApiError && failure.status === 409) setStuckIntent(input);
          setCheckoutError(
            failure instanceof BillingApiError || failure instanceof ApiClientError
              ? failure.message
              : 'checkout could not be started',
          );
        });
    },
    [authenticated, onCheckoutStart, requireLogin],
  );

  return (
    <section className="pricing-page">
      <header>
        <h1>Plans</h1>
      </header>

      {checkoutError ? (
        <p className="pricing-checkout-error" role="alert">
          {checkoutError}
        </p>
      ) : null}

      {stuckIntent ? <div><p>A previous checkout may still exist. Check your billing before starting another purchase.</p>
        <button type="button" onClick={() => { abandonCheckoutIntent(stuckIntent); setStuckIntent(null); setCheckoutError(''); }}>Forget previous checkout and start over</button></div> : null}

      {unavailable ? (
        <p className="pricing-unavailable" role="alert" data-state="Unavailable">
          Plans could not be loaded right now. Please try again in a moment.
        </p>
      ) : null}

      {entries && entries.length === 0 ? (
        <p className="pricing-withheld" data-state="Withheld">
          No plan is open for sign-up yet. Plans will appear here as soon as they are approved
          for sale.
        </p>
      ) : null}

      {entries && entries.length > 0 ? (
        <div className="pricing-cards">
          {entries.map((entry) => (
            <PricingCard
              key={entry.id}
              entry={entry}
              authenticated={authenticated}
              busy={busyEntryId === entry.id}
              onSubscribe={beginCheckout}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

export default PricingPage;
